import { cn } from '@/lib/cn';
import { getFaviconUrl } from '@/utils/faviconCache';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import Image from 'next/image';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  StackedSourceAvatarSize,
  StackedSourceAvatarsProps,
} from './evidence-prompts-bar.types';

const AVATAR_CONFIG: Record<
  StackedSourceAvatarSize,
  { px: number; sizeClass: string; gapClass: string; textClass: string }
> = {
  sm: { px: 16, sizeClass: 'size-4', gapClass: '-space-x-1', textClass: 'text-[8px]' },
  md: {
    px: 25,
    sizeClass: 'size-[25px]',
    gapClass: '-space-x-1.5',
    textClass: 'text-[10px]',
  },
  lg: { px: 32, sizeClass: 'size-8', gapClass: '-space-x-2.5', textClass: 'text-xs' },
};

function FaviconCircle({
  domain,
  displayName,
  size,
  disableTooltip = false,
}: {
  domain: string;
  displayName?: string;
  size: StackedSourceAvatarSize;
  disableTooltip?: boolean;
}) {
  const [hasError, setHasError] = useState(false);
  const { px, sizeClass, textClass } = AVATAR_CONFIG[size];
  const label = displayName?.trim() || domain;

  if (!domain) return null;

  const avatar = hasError ? (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-background-secondary',
        sizeClass
      )}
    >
      <span className={cn('font-medium text-text-secondary', textClass)}>
        {label.charAt(0).toUpperCase()}
      </span>
    </div>
  ) : (
    <Image
      src={getFaviconUrl(domain)}
      alt=""
      width={px}
      height={px}
      unoptimized
      className={cn('shrink-0 rounded-full bg-surface', sizeClass)}
      onError={() => setHasError(true)}
    />
  );

  if (disableTooltip) {
    return (
      <span
        className={cn(
          'relative inline-flex origin-center transition-transform duration-150 ease-out',
          'hover:z-10 hover:scale-125'
        )}
      >
        {avatar}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'relative inline-flex origin-center transition-transform duration-150 ease-out',
              // Lift the hovered favicon above its neighbours in the
              // negative-gap stack and scale it up so the user can
              // read the brand more easily while its tooltip appears.
              'hover:z-10 hover:scale-125'
            )}
          >
            {avatar}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function StackedSourceAvatars({
  sources,
  maxVisible = 3,
  size = 'sm',
  overflowVariant = 'default',
  disableTooltips = false,
  className,
}: StackedSourceAvatarsProps) {
  const intl = useIntl();

  if (sources.length === 0) return null;

  const visible = sources.slice(0, maxVisible);
  const remaining = Math.max(0, sources.length - maxVisible);
  const { gapClass } = AVATAR_CONFIG[size];
  const overflowSourceLabels = sources
    .slice(maxVisible)
    .map((s) => s.displayName?.trim() || s.domain);

  const overflowChipClasses = cn(
    'relative inline-flex shrink-0 origin-center items-center justify-center rounded-full',
    'transition-transform duration-150 ease-out hover:z-10 hover:scale-125',
    overflowVariant === 'pro' ? 'bg-tier-pro' : 'bg-background-secondary',
    AVATAR_CONFIG[size].sizeClass
  );
  const overflowLabelClasses = cn(
    'font-medium',
    overflowVariant === 'pro' ? 'text-text-primary-on-dark' : 'text-text-secondary',
    AVATAR_CONFIG[size].textClass
  );

  return (
    <div className={cn('flex items-center', className)}>
      <div className={cn('flex', gapClass)}>
        {visible.map((source, index) => (
          <FaviconCircle
            key={`${source.domain}-${index}`}
            domain={source.domain}
            displayName={source.displayName}
            size={size}
            disableTooltip={disableTooltips}
          />
        ))}
        {remaining > 0 &&
          (disableTooltips ? (
            <span className={overflowChipClasses}>
              <span className={overflowLabelClasses}>
                <FormattedMessage
                  defaultMessage="+{count}"
                  id="PoVZScB+89"
                  description="Badge showing how many additional evidence sources are hidden behind the avatar stack"
                  values={{ count: remaining }}
                />
              </span>
            </span>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className={overflowChipClasses}>
                    <span className={overflowLabelClasses}>
                      <FormattedMessage
                        defaultMessage="+{count}"
                        id="PoVZScB+89"
                        description="Badge showing how many additional evidence sources are hidden behind the avatar stack"
                        values={{ count: remaining }}
                      />
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {intl.formatList(overflowSourceLabels)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
      </div>
    </div>
  );
}
